import React from 'react';
import { useTranslation } from 'react-i18next';
import { BasicCheckbox } from '@/components/BasicCheckbox';
import { LeagueRow } from '@/components/LeagueRow';
import { LEAGUE_STATUSES } from '@/constants';
import type { AdminLeague } from '@/types/league';
import styles from './styles.module.scss';

export interface AdminLeagueRowProps {
  league: AdminLeague;
  /** A toggle request for this league is in flight — the row is dimmed. */
  busy: boolean;
  onToggleEnabled: () => void;
}

function statusClass(status: AdminLeague['status']): string {
  if (status === LEAGUE_STATUSES.ACTIVE) return styles.badgeSuccess;
  if (status === LEAGUE_STATUSES.PLANNED) return styles.badgeAccent;
  return styles.badgeNeutral;
}

/**
 * Admin league row inside a country card: logo, name, season · external id ·
 * status badge, and the Enabled checkbox on the right.
 */
export const AdminLeagueRow: React.FC<AdminLeagueRowProps> = ({
  league,
  busy,
  onToggleEnabled,
}) => {
  const { t } = useTranslation('admin');
  const enabledId = `league-${league.id}`;

  return (
    <LeagueRow
      name={league.name}
      logoUrl={league.logoUrl}
      className={[
        league.isEnabled && styles.enabled,
        busy && styles.busy,
      ].filter(Boolean).join(' ')}
      testId={`admin-league-row-${league.id}`}
      meta={(
        <>
          <span>{league.season} · ID {league.externalId}</span>
          {league.status && (
            <span className={`${styles.badge} ${statusClass(league.status)}`}>{league.status}</span>
          )}
        </>
      )}
      trailing={(
        <span className={styles.control}>
          <label className={styles.controlLabel} htmlFor={enabledId}>
            {t('admin:leagueEnabled')}
          </label>
          <BasicCheckbox
            id={enabledId}
            checked={league.isEnabled}
            onChange={onToggleEnabled}
            color="green"
            testId={`admin-league-enabled-${league.id}`}
          />
        </span>
      )}
    />
  );
};

export default AdminLeagueRow;
